/**
 * Recursively replaces undefined values with null so data is valid for Firestore writes.
 * Firestore does not allow undefined as a field value.
 * @param {any} value - Data to sanitize (object, array, or primitive)
 * @returns {any} Sanitized data safe for Firestore
 */
export const sanitizeForFirestore = (value) => {
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sanitizeForFirestore);
  const result = {};
  for (const [k, v] of Object.entries(value)) {
    result[k] = sanitizeForFirestore(v);
  }
  return result;
};

/**
 * Serializes Firestore data to make it Redux-safe
 * Converts Firestore Timestamps to ISO strings (handles nested objects and arrays)
 * @param {any} data - Firestore data (can be object, array, or primitive)
 * @returns {any} Serialized data
 */
export const serializeFirestoreData = (data) => {
  if (!data) return data;
  
  // Handle Firestore Timestamp objects
  if (data && typeof data === 'object' && data.seconds !== undefined && data.nanoseconds !== undefined) {
    // This is a Firestore Timestamp
    return new Date(data.seconds * 1000 + data.nanoseconds / 1000000).toISOString();
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => serializeFirestoreData(item));
  }
  
  // Handle objects (but not Date, RegExp, etc.)
  if (data.constructor === Object) {
    const serialized = {};
    Object.keys(data).forEach(key => {
      serialized[key] = serializeFirestoreData(data[key]);
    });
    return serialized;
  }
  
  // Return primitives and other types as-is
  return data;
};
